import type { KeyboardEvent } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface SelectProps {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  // Приходит из Field (render-prop): id связывает label с триггером, aria-* —
  // валидность/подсказку. При standalone-использовании задай aria-label.
  id?: string
  'aria-label'?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
  disabled?: boolean
  // Текст триггера, когда value не совпадает ни с одной опцией (плейсхолдер).
  placeholder?: string
  className?: string
}

// Стилизованный select на listbox-паттерне: триггер-combobox в стиле Input +
// плавающий список в стиле Menu. Фокус остаётся на триггере, активная опция —
// через aria-activedescendant (клавиатура: стрелки/Home/End/Enter/Esc/type-ahead).
// Заменяет нативный <select>, который нельзя темизировать под токен-слой.
export function Select({
  value,
  onChange,
  options,
  id,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedby,
  disabled = false,
  placeholder,
  className,
}: SelectProps) {
  const uid = useId()
  const listId = `${uid}-list`
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  const selectedIndex = options.findIndex((o) => o.value === value)
  const [active, setActive] = useState(selectedIndex < 0 ? 0 : selectedIndex)
  const typeahead = useRef<{ buffer: string; timer: ReturnType<typeof setTimeout> | undefined }>({
    buffer: '',
    timer: undefined,
  })

  const selected = selectedIndex < 0 ? undefined : options[selectedIndex]
  const optionId = (i: number) => `${uid}-opt-${i}`

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  const openList = () => {
    if (disabled) return
    setActive(selectedIndex < 0 ? firstEnabled() : selectedIndex)
    setOpen(true)
  }

  const closeList = (focusTrigger = true) => {
    setOpen(false)
    if (focusTrigger) triggerRef.current?.focus()
  }

  const commit = (i: number) => {
    const opt = options[i]
    if (!opt || opt.disabled) return
    onChange(opt.value)
    closeList()
  }

  const firstEnabled = () => {
    const i = options.findIndex((o) => !o.disabled)
    return i < 0 ? 0 : i
  }
  const lastEnabled = () => {
    for (let i = options.length - 1; i >= 0; i--) if (!options[i]?.disabled) return i
    return active
  }
  const step = (from: number, dir: 1 | -1) => {
    for (let i = from + dir; i >= 0 && i < options.length; i += dir) {
      if (!options[i]?.disabled) return i
    }
    return from
  }

  // Type-ahead: копим введённые символы (сброс через 500мс), прыгаем на первую
  // совпадающую по префиксу опцию.
  const onType = (char: string) => {
    const ta = typeahead.current
    if (ta.timer) clearTimeout(ta.timer)
    ta.buffer += char.toLowerCase()
    ta.timer = setTimeout(() => {
      ta.buffer = ''
    }, 500)
    const match = options.findIndex(
      (o) => !o.disabled && o.label.toLowerCase().startsWith(ta.buffer),
    )
    if (match >= 0) setActive(match)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        openList()
      }
      return
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActive((i) => step(i, 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActive((i) => step(i, -1))
        break
      case 'Home':
        e.preventDefault()
        setActive(firstEnabled())
        break
      case 'End':
        e.preventDefault()
        setActive(lastEnabled())
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        commit(active)
        break
      case 'Escape':
        e.preventDefault()
        closeList()
        break
      case 'Tab':
        setOpen(false)
        break
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) onType(e.key)
    }
  }

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? optionId(active) : undefined}
        disabled={disabled}
        {...(id ? { id } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        {...(ariaInvalid ? { 'aria-invalid': true } : {})}
        {...(ariaDescribedby ? { 'aria-describedby': ariaDescribedby } : {})}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        className={cn(
          'flex w-full items-center gap-2 rounded-control border bg-bg-sunken px-4 pt-3 pb-[14px] text-left transition-colors focus:outline-none disabled:opacity-50',
          open ? 'border-border-accent' : 'border-border-default focus:border-border-accent',
        )}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            selected ? 'text-text-primary' : 'text-text-tertiary',
          )}
        >
          {selected?.label ?? placeholder ?? ''}
        </span>
        <Icon
          name="chevron-down"
          size={16}
          className={cn('shrink-0 text-text-tertiary transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div
          role="listbox"
          id={listId}
          className="absolute z-30 mt-1 flex max-h-64 w-full flex-col overflow-auto overscroll-contain rounded-card border border-border-default bg-bg-surface p-1 shadow-lg"
        >
          {options.map((o, i) => (
            <button
              key={o.value}
              type="button"
              id={optionId(i)}
              role="option"
              tabIndex={-1}
              aria-selected={o.value === value}
              {...(o.disabled ? { 'aria-disabled': true } : {})}
              onMouseEnter={() => !o.disabled && setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => commit(i)}
              className={cn(
                'flex w-full items-center gap-2 rounded-control px-2.5 py-2 text-left text-sm text-text-secondary transition-colors',
                i === active && !o.disabled && 'bg-bg-raised text-text-primary',
                o.disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <span className="min-w-0 flex-1 truncate">{o.label}</span>
              {o.value === value && (
                <Icon name="check" size={14} className="shrink-0 text-accent" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
